// frontend/src/utils/columnAggregations.ts
//
// Cálculo puro de los totales de columna. Vive separado del hook para poder
// cubrirlo con vitest sin renderizar React.
import type { AggColumn, AggFn, AggResult, ColumnAggState } from '@/types'

const DEFAULT_LOCALE = 'es-ES'

/** Símbolo que precede al valor en la fila de pie cuando la función no es suma. */
export const AGG_SYMBOL: Record<AggFn, string> = {
  sum: 'Σ',
  avg: 'Ø',
  min: '↓',
  max: '↑',
  count: '#',
  countDistinct: '≠',
}

/**
 * Saca el número de una fila para una columna. Devuelve `null` si el valor no
 * es utilizable (ausente, no numérico o NaN), para que el llamante lo descarte
 * en lugar de contaminar la agregación con ceros inventados.
 */
function readValue<T>(row: T, col: AggColumn<T>): number | null {
  const raw = col.value
    ? col.value(row)
    : (row as Record<string, unknown>)[col.valueKey ?? col.key]
  if (raw == null) return null
  const n = typeof raw === 'number' ? raw : Number(raw)
  return Number.isFinite(n) ? n : null
}

/**
 * Deduplica por `col.dedupeKey`: conserva la **primera** fila de cada valor
 * distinto de la clave. Sin clave devuelve las filas tal cual.
 *
 * Quedarse la primera y no, por ejemplo, el máximo, es correcto porque la
 * métrica deduplicada es un atributo de la entidad y vale lo mismo en todas sus
 * filas. Si algún día dejara de ser así, el total cambiaría sin avisar; por eso
 * hay un test de integración que vigila que la suma deduplicada siga siendo un
 * orden de magnitud menor que la ingenua.
 *
 * Las filas cuya clave viene vacía (`null`/`undefined`/`''`) se conservan todas:
 * no se pueden agrupar, y descartarlas silenciosamente sería peor que contarlas.
 */
function dedupeRows<T>(rows: T[], dedupeKey: string): T[] {
  const seen = new Set<unknown>()
  const out: T[] = []
  for (const row of rows) {
    const k = (row as Record<string, unknown>)[dedupeKey]
    if (k == null || k === '') {
      out.push(row)
      continue
    }
    if (seen.has(k)) continue
    seen.add(k)
    out.push(row)
  }
  return out
}

/**
 * Cuenta valores únicos de una columna leyendo el valor **en crudo**: los
 * códigos de artículo son texto, así que no puede pasar por el reductor
 * numérico. Ignora vacíos.
 */
function countDistinctValues<T>(rows: T[], col: AggColumn<T>): number {
  const field = col.distinctKey ?? col.valueKey ?? col.key
  const seen = new Set<unknown>()
  for (const row of rows) {
    const raw = (row as Record<string, unknown>)[field]
    if (raw == null || raw === '') continue
    seen.add(raw)
  }
  return seen.size
}

/**
 * Reduce una lista de valores ya validados. Con la lista vacía devuelve 0 en
 * todas las funciones: el llamante distingue el caso por `count === 0`.
 *
 * El mínimo y el máximo se calculan con un bucle explícito y no con
 * `Math.min(...values)`, que revienta la pila con datasets de decenas de miles
 * de filas.
 */
function reduceValues(values: number[], fn: AggFn): number {
  if (fn === 'count') return values.length
  if (values.length === 0) return 0

  if (fn === 'min' || fn === 'max') {
    let acc = values[0]
    for (let i = 1; i < values.length; i++) {
      if (fn === 'min' ? values[i] < acc : values[i] > acc) acc = values[i]
    }
    return acc
  }

  let total = 0
  for (const v of values) total += v
  return fn === 'avg' ? total / values.length : total
}

/**
 * Agrega las columnas que tengan función activa en `state`. Las demás ni se
 * recorren: activar un total sobre una tabla de 20 columnas sólo cuesta el
 * recorrido de las columnas realmente pedidas.
 */
export function computeAggregations<T>(
  rows: T[],
  columns: AggColumn<T>[],
  state: ColumnAggState,
): Record<string, AggResult> {
  const out: Record<string, AggResult> = {}

  for (const col of columns) {
    const fn = state[col.key]
    if (!fn) continue

    // La deduplicación se aplica antes de cualquier lectura de valores, así que
    // afecta igual a la suma, a la media y al recuento.
    const scoped = col.dedupeKey ? dedupeRows(rows, col.dedupeKey) : rows
    const dedupedCount = col.dedupeKey ? scoped.length : undefined

    if (fn === 'countDistinct') {
      const value = countDistinctValues(scoped, col)
      out[col.key] = { kind: 'number', fn, value, count: value, dedupedCount }
      continue
    }

    if (col.currencyKey) {
      // Un pedido en USD y otro en EUR no se pueden sumar: se agrupa por divisa.
      const byCurrency = new Map<string, number[]>()
      for (const row of scoped) {
        const v = readValue(row, col)
        if (v === null) continue
        const cur = String((row as Record<string, unknown>)[col.currencyKey] ?? '')
        const list = byCurrency.get(cur)
        if (list) list.push(v)
        else byCurrency.set(cur, [v])
      }
      let count = 0
      const parts = [...byCurrency.entries()].map(([currency, values]) => {
        count += values.length
        return { currency, value: reduceValues(values, fn) }
      })
      parts.sort((a, b) => b.value - a.value)
      out[col.key] = { kind: 'byCurrency', fn, parts, count }
      continue
    }

    const values: number[] = []
    for (const row of scoped) {
      const v = readValue(row, col)
      if (v !== null) values.push(v)
    }
    out[col.key] = {
      kind: 'number', fn, value: reduceValues(values, fn), count: values.length, dedupedCount,
    }
  }

  return out
}

/**
 * Formatea un valor agregado. Los recuentos siempre salen como entero, aunque
 * la columna declare decimales.
 *
 * Con `fixed` se rellenan siempre los decimales pedidos (1.204.320,50), como
 * corresponde a un importe. Sin él sólo se muestran los necesarios, que es lo
 * habitual para cantidades (128.430 en vez de 128.430,00).
 */
export function formatAggValue(
  value: number,
  fn: AggFn,
  decimals = 0,
  fixed = false,
  locale = DEFAULT_LOCALE,
): string {
  const d = fn === 'count' || fn === 'countDistinct' ? 0 : decimals
  // El runtime acepta useGrouping: 'always' desde ES2023, pero los tipos de la
  // librería de TypeScript de este proyecto aún lo declaran como boolean.
  const opts: Omit<Intl.NumberFormatOptions, 'useGrouping'> & {
    useGrouping?: 'always' | 'auto' | 'min2' | boolean
  } = {
    minimumFractionDigits: fixed ? d : 0,
    maximumFractionDigits: d,
    // es-ES agrupa por defecto en modo «min2», que NO pone separador en los
    // números de cuatro cifras: el mismo total salía «25.000» o «5000» según el
    // valor. Con 'always' el pie lee igual que las celdas.
    useGrouping: 'always',
  }
  return value.toLocaleString(locale, opts as Intl.NumberFormatOptions)
}
